/**
 * Input aggregation with pointer-lock mouse look.
 *
 * Palimpsest has no HUD-driven controls and no menus in the way — you click once to
 * enter the world and then it is just body and city. Input therefore stays tiny:
 * a movement axis, a look delta consumed each frame, and a couple of edge-triggered
 * actions (jump, sprint). Keeping raw DOM handling isolated here means the player
 * controller never touches the DOM.
 */
import { Emitter } from '@/utils/events';
import type { AppEvents } from '@/core/types';
import { IS_TOUCH } from '@/utils/device';
import { TouchControls } from './touch-controls';

export class Input {
  private keys = new Set<string>();
  private lookDX = 0;
  private lookDY = 0;
  private jumpQueued = false;
  locked = false;

  /** On touch devices only: the on-screen overlay whose queries are merged below.
   *  Null on desktop, which keeps every desktop code path byte-for-byte unchanged. */
  private touch: TouchControls | null = null;
  private touchEntered = false;

  constructor(
    private domElement: HTMLElement,
    private events: Emitter<AppEvents>,
  ) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);

    if (IS_TOUCH) {
      // No pointer lock exists on touch: the first tap counts as "entering", and the
      // floating overlay takes over look/move/jump from there on.
      this.touch = new TouchControls();
      domElement.addEventListener('touchend', this.onFirstTouch, { passive: false });
    } else {
      domElement.addEventListener('click', this.requestLock);
      document.addEventListener('pointerlockchange', this.onLockChange);
      document.addEventListener('mousemove', this.onMouseMove);
    }
  }

  /**
   * Touch "enter": stands in for the desktop pointer-lock so the veil dismissal,
   * audio unlock and `hasEntered` gating all fire through the same event exactly once.
   * We never call requestPointerLock on touch devices.
   */
  private onFirstTouch = (e: TouchEvent): void => {
    if (this.touchEntered) return;
    this.touchEntered = true;
    this.locked = true; // so any locked-gating downstream behaves as it does on desktop
    this.touch?.enable();
    this.events.emit('app:pointerlock', { locked: true });
    e.preventDefault();
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    // Don't swallow devtools/refresh shortcuts.
    if (e.metaKey || e.ctrlKey) return;
    this.keys.add(e.code);
    if (e.code === 'Space') {
      this.jumpQueued = true;
      e.preventDefault();
    }
  };
  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };
  private onBlur = (): void => {
    this.keys.clear();
  };

  private requestLock = (): void => {
    if (!this.locked) this.domElement.requestPointerLock();
  };
  private onLockChange = (): void => {
    this.locked = document.pointerLockElement === this.domElement;
    this.events.emit('app:pointerlock', { locked: this.locked });
  };
  private onMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    this.lookDX += e.movementX;
    this.lookDY += e.movementY;
  };

  /* -------- queried by the player controller each frame -------- */

  /** Forward(+)/back(-) and right(+)/left(-) intent in [-1,1]. */
  moveAxis(): { x: number; z: number } {
    // The floating stick wins whenever it is being held; otherwise the keyboard
    // path below runs exactly as it always has.
    const touchAxis = this.touch?.moveAxis();
    if (touchAxis) return touchAxis;
    let x = 0;
    let z = 0;
    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) z -= 1;
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) z += 1;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) x -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) x += 1;
    const len = Math.hypot(x, z);
    if (len > 1) {
      x /= len;
      z /= len;
    }
    return { x, z };
  }

  get sprinting(): boolean {
    if (this.touch?.sprinting) return true;
    return this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
  }

  /** Consume accumulated look for this frame (mouse + touch summed). */
  consumeLook(): { dx: number; dy: number } {
    const d = { dx: this.lookDX, dy: this.lookDY };
    this.lookDX = 0;
    this.lookDY = 0;
    if (this.touch) {
      const t = this.touch.consumeLook();
      d.dx += t.dx;
      d.dy += t.dy;
    }
    return d;
  }

  /** Consume a queued jump (edge-triggered); keyboard OR touch. */
  consumeJump(): boolean {
    let jumped = false;
    if (this.jumpQueued) {
      this.jumpQueued = false;
      jumped = true;
    }
    // Always consume the touch flag too, so it never sticks across frames.
    if (this.touch?.consumeJump()) jumped = true;
    return jumped;
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.domElement.removeEventListener('click', this.requestLock);
    this.domElement.removeEventListener('touchend', this.onFirstTouch);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    document.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('blur', this.onBlur);
    this.touch?.dispose();
  }
}
